import { useCallback, useContext, useMemo } from "react";
import { Controller, UseFormSetValue, UseFormWatch } from "react-hook-form";
import {
    Alert,
    Card,
    Col,
    Flex,
    Form,
    InputNumber,
    Row,
    Select,
    Switch,
    Typography,
} from "antd";
import {
    AppstoreOutlined,
    BankOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    GiftOutlined,
    SafetyCertificateOutlined,
    TagOutlined,
    ThunderboltOutlined,
    TrophyOutlined,
} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";

import {
    ICity,
    ICityConfig,
    ICitySystem,
    ICityUpcomingConfigAndTerms,
} from "../../../../../../interfaces";
import { ICityFormStepProps, ICityInputs } from "../../../../interfaces";
import { EModule } from "../../../../../../enums";
import useAuth from "../../../../../../hooks/useAuth";
import useModules from "../../../../../../hooks/useModules";
import { ThemeContext } from "../../../../../../contexts/ThemeContext";
import DatePicker from "../../../../../../components/DatePicker";
import FieldsGroupTitle from "../../../FieldsDivider";
import FieldsCard from "../../../FieldsCard";
import UpcomingConfigAlert from "../../../UpcomingConfigAlert";
import { MODULES } from "../../useCityForm";
import UpcomingConfigCarousel from "../../UpcomingConfigCarousel";
import useConfigStep from "./useConfigStep";
import ConfigUnlimitedField from "./components/ConfigUnlimitedField";
import ConfigDivisorField from "./components/ConfigDivisorField";

interface IProps extends ICityFormStepProps {
    needsToInformPointsConfigEffectiveDate: boolean;
    needsToInformTicketsConfigEffectiveDate: boolean;
    upcomingConfig: ICityUpcomingConfigAndTerms | undefined;
    systems: ICitySystem[];
    alreadyIncludedModules: EModule[];
    formHasInvoiceNFCModule: boolean;
    formHasInvoiceNFSModule: boolean;
    formHasRewardModule: boolean;
    formHasTaxesModule: boolean;
    formHasTaxesAutoModule: boolean;
    city?: ICity;
    watch: UseFormWatch<ICityInputs>;
    setValue: UseFormSetValue<ICityInputs>;
}

export default function ConfigStep({
    control,
    errors,
    needsToInformPointsConfigEffectiveDate,
    needsToInformTicketsConfigEffectiveDate,
    upcomingConfig,
    systems,
    alreadyIncludedModules,
    formHasInvoiceNFCModule,
    formHasInvoiceNFSModule,
    formHasRewardModule,
    formHasTaxesModule,
    formHasTaxesAutoModule,
    city,
    watch,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const { handleRemoveModuleOnCity } = useConfigStep(
        needsToInformPointsConfigEffectiveDate,
        needsToInformTicketsConfigEffectiveDate,
        city
    );
    const { cityModules } = useModules();
    const { user } = useAuth();

    const isAdmin = useMemo(() => {
        return user?.role?.id === 1;
    }, [user]);

    const showNFSConfig = useMemo(() => formHasInvoiceNFSModule, [formHasInvoiceNFSModule]);
    const showNFCConfig = useMemo(() => formHasInvoiceNFCModule, [formHasInvoiceNFCModule]);
    const showTaxesConfig = useMemo(() => formHasTaxesModule, [formHasTaxesModule]);
    const showTaxesAutoConfig = useMemo(() => formHasTaxesAutoModule, [formHasTaxesAutoModule]);
    const showRewardConfig = useMemo(() => formHasRewardModule, [formHasRewardModule]);

    const hasAtLeastOnePointsGenerationModule = useMemo(() => {
        return (
            formHasInvoiceNFSModule ||
            formHasInvoiceNFCModule ||
            formHasTaxesModule ||
            formHasTaxesAutoModule
        );
    }, [formHasInvoiceNFSModule, formHasInvoiceNFCModule, formHasTaxesModule, formHasTaxesAutoModule]);

    const hasAtLeastOneInvoiceModule = useMemo(() => {
        return formHasInvoiceNFSModule || formHasInvoiceNFCModule;
    }, [formHasInvoiceNFSModule, formHasInvoiceNFCModule]);

    const divisorColProps = useMemo(() => {
        const count = (showNFSConfig ? 1 : 0) + (showNFCConfig ? 1 : 0) + (showTaxesConfig ? 1 : 0);
        if (count >= 3) return { xs: 24, sm: 12, md: 8 };
        if (count === 2) return { xs: 24, sm: 12 };
        return { xs: 24 };
    }, [showNFSConfig, showNFCConfig, showTaxesConfig]);

    const invoiceColProps = useMemo(() => {
        const count = (showNFSConfig ? 1 : 0) + (showNFCConfig ? 1 : 0);
        if (count >= 2) return { xs: 24, sm: 12 };
        return { xs: 24 };
    }, [showNFSConfig, showNFCConfig]);

    const disableInputPoints = useCallback(
        (upcomingConfigField: keyof ICityConfig, cityField: keyof ICity) => {
            return (
                !!upcomingConfig?.points_config?.find((cfg) => cfg[upcomingConfigField]) &&
                city &&
                !city[cityField]
            );
        },
        [upcomingConfig, city]
    );

    const disableInputTickets = useCallback(
        (upcomingConfigField: keyof ICityConfig, cityField: keyof ICity) => {
            return (
                !!upcomingConfig?.tickets_config?.find((cfg) => cfg[upcomingConfigField]) &&
                city &&
                !city[cityField]
            );
        },
        [upcomingConfig, city]
    );

    const isUnlimitedNfsPoints = watch("is_unlimited_nfs_divider_points");
    const isUnlimitedNfcPoints = watch("is_unlimited_nfc_divider_points");
    const isUnlimitedTaxePoints = watch("is_unlimited_taxe_divider_points");
    const isUnlimitedNfsByCnpjPoints = watch("is_unlimited_by_cnpj_nfs_divider_points");
    const isUnlimitedNfcByCnpjPoints = watch("is_unlimited_by_cnpj_nfc_divider_points");

    const isUnlimitedNfsTickets = watch("is_unlimited_nfs_divider_ticket");
    const isUnlimitedNfcTickets = watch("is_unlimited_nfc_divider_ticket");
    const isUnlimitedTaxeTickets = watch("is_unlimited_taxe_divider_ticket");
    const isUnlimitedNfsByCnpjTickets = watch("is_unlimited_by_cnpj_nfs_divider_ticket");
    const isUnlimitedNfcByCnpjTickets = watch("is_unlimited_by_cnpj_nfc_divider_ticket");

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {(isAdmin || (systems.length > 0 && showNFSConfig)) && (
                <div>
                    <FieldsGroupTitle title="Aplicativo e Integrações" />
                    <Row gutter={[16, 16]}>
                        <FieldsCard
                            title="Módulos Habilitados e Sistema Fiscal"
                            icon={<AppstoreOutlined style={{ color: "#2563eb", fontSize: 16 }} />}
                            subtitle="Defina quais funcionalidades estão ativas para este município"
                            span={24}
                        >
                            {isAdmin && (
                                <Col xs={24} md={systems.length > 0 && showNFSConfig ? 12 : 24}>
                                    <Form.Item
                                        label="Módulos"
                                        required
                                        validateStatus={errors.modules ? "error" : "success"}
                                        help={errors.modules?.message}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Controller
                                            name="modules"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    placeholder="Selecione os módulos"
                                                    mode="multiple"
                                                    allowClear={alreadyIncludedModules.length === 0}
                                                    onDeselect={(value) => handleRemoveModuleOnCity(value)}
                                                    style={{ width: "100%" }}
                                                >
                                                    {MODULES.map((module) => (
                                                        <Select.Option
                                                            key={module.name}
                                                            value={module.name}
                                                        >
                                                            {module.name === EModule.INVOICE_NFS
                                                                ? `${module.label} (${city?.system_name ?? "--"})`
                                                                : module.label}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                    </Form.Item>
                                </Col>
                            )}

                            {systems.length > 0 && showNFSConfig && (
                                <Col xs={24} md={isAdmin ? 12 : 24}>
                                    <Form.Item
                                        label="Sistema Fiscal Integrado"
                                        required
                                        validateStatus={errors.system_id ? "error" : "success"}
                                        help={errors.system_id?.message}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Controller
                                            name="system_id"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={systems.map((system) => ({
                                                        value: system.id,
                                                        label: system.name,
                                                    }))}
                                                    placeholder="Selecione o sistema fiscal"
                                                    style={{ width: "100%" }}
                                                />
                                            )}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                        </FieldsCard>
                    </Row>
                </div>
            )}

            {showRewardConfig && (
                <div>
                    <FieldsGroupTitle title="Pontos" showDivider={isAdmin || systems.length > 0} />

                    {hasAtLeastOnePointsGenerationModule ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                            <Row gutter={[16, 16]}>
                                <FieldsCard
                                    title="Divisores de Pontos"
                                    icon={<GiftOutlined style={{ color: "#10b981", fontSize: 16 }} />}
                                    subtitle="Valor em reais (R$) necessário para gerar 1 ponto"
                                    span={12}
                                >
                                    {showNFSConfig && (
                                        <Col {...divisorColProps}>
                                            <Controller
                                                name="nfs_points_divider"
                                                control={control}
                                                render={({ field }) => (
                                                    <ConfigDivisorField
                                                        label="NFS"
                                                        tooltip="A cada R$ X em NFSs, o usuário ganhará 1 ponto."
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        error={errors.nfs_points_divider}
                                                        disabled={disableInputPoints("nfs_divider", "nfs_points_divider")}
                                                    />
                                                )}
                                            />
                                        </Col>
                                    )}

                                    {showNFCConfig && (
                                        <Col {...divisorColProps}>
                                            <Controller
                                                name="nfc_points_divider"
                                                control={control}
                                                render={({ field }) => (
                                                    <ConfigDivisorField
                                                        label="NFC"
                                                        tooltip="A cada R$ X em NFCs, o usuário ganhará 1 ponto."
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        error={errors.nfc_points_divider}
                                                        disabled={disableInputPoints("nfc_divider", "nfc_points_divider")}
                                                    />
                                                )}
                                            />
                                        </Col>
                                    )}

                                    {showTaxesConfig && (
                                        <Col {...divisorColProps}>
                                            <Controller
                                                name="taxe_points_divider"
                                                control={control}
                                                render={({ field }) => (
                                                    <ConfigDivisorField
                                                        label="Impostos Municipais"
                                                        tooltip="A cada R$ X em Impostos, o usuário ganhará 1 ponto."
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        error={errors.taxe_points_divider}
                                                        disabled={disableInputPoints("taxe_divider", "taxe_points_divider")}
                                                    />
                                                )}
                                            />
                                        </Col>
                                    )}
                                </FieldsCard>

                                <FieldsCard
                                    title="Valor Máximo para Gerar Pontos"
                                    icon={<DollarOutlined style={{ color: "#10b981", fontSize: 16 }} />}
                                    subtitle="Teto máximo por documento considerado para pontuação"
                                    span={12}
                                >
                                    {showNFSConfig && (
                                        <Col {...divisorColProps}>
                                            <Controller
                                                name="nfs_max_amount_points"
                                                control={control}
                                                render={({ field }) => (
                                                    <Controller
                                                        name="is_unlimited_nfs_divider_points"
                                                        control={control}
                                                        render={({ field: unlimitedField }) => (
                                                            <ConfigUnlimitedField
                                                                label="NFS"
                                                                tooltip="A partir de R$ X em NFSs, não são contabilizados mais pontos."
                                                                unlimitedLabel="Sem valor máx."
                                                                isUnlimited={isUnlimitedNfsPoints}
                                                                onUnlimitedChange={unlimitedField.onChange}
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                error={errors.nfs_max_amount_points}
                                                                disabled={disableInputPoints("nfs_max_amount", "nfs_max_amount_points")}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Col>
                                    )}

                                    {showNFCConfig && (
                                        <Col {...divisorColProps}>
                                            <Controller
                                                name="nfc_max_amount_points"
                                                control={control}
                                                render={({ field }) => (
                                                    <Controller
                                                        name="is_unlimited_nfc_divider_points"
                                                        control={control}
                                                        render={({ field: unlimitedField }) => (
                                                            <ConfigUnlimitedField
                                                                label="NFC"
                                                                tooltip="A partir de R$ X em NFCs, não são contabilizados mais pontos."
                                                                unlimitedLabel="Sem valor máx."
                                                                isUnlimited={isUnlimitedNfcPoints}
                                                                onUnlimitedChange={unlimitedField.onChange}
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                error={errors.nfc_max_amount_points}
                                                                disabled={disableInputPoints("nfc_max_amount", "nfc_max_amount_points")}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Col>
                                    )}

                                    {showTaxesConfig && (
                                        <Col {...divisorColProps}>
                                            <Controller
                                                name="taxe_max_amount_points"
                                                control={control}
                                                render={({ field }) => (
                                                    <Controller
                                                        name="is_unlimited_taxe_divider_points"
                                                        control={control}
                                                        render={({ field: unlimitedField }) => (
                                                            <ConfigUnlimitedField
                                                                label="Impostos Municipais"
                                                                tooltip="A partir de R$ X em Impostos, não são contabilizados mais pontos."
                                                                unlimitedLabel="Sem valor máx."
                                                                isUnlimited={isUnlimitedTaxePoints}
                                                                onUnlimitedChange={unlimitedField.onChange}
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                error={errors.taxe_max_amount_points}
                                                                disabled={disableInputPoints("taxe_max_amount", "taxe_max_amount_points")}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Col>
                                    )}
                                </FieldsCard>
                            </Row>

                            <Row gutter={[16, 16]}>
                                {hasAtLeastOneInvoiceModule && (
                                    <FieldsCard
                                        title="Quantidade Mensal Limite por Estabelecimento"
                                        icon={<BankOutlined style={{ color: "#10b981", fontSize: 16 }} />}
                                        subtitle="Máximo de notas de um mesmo CNPJ/mês que geram pontos"
                                        span={12}
                                    >
                                        {showNFSConfig && (
                                            <Col {...invoiceColProps}>
                                                <Controller
                                                    name="nfs_max_month_quantity_by_cnpj_points"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Controller
                                                            name="is_unlimited_by_cnpj_nfs_divider_points"
                                                            control={control}
                                                            render={({ field: unlimitedField }) => (
                                                                <ConfigUnlimitedField
                                                                    label="NFS"
                                                                    tooltip="Quantidade limite de NFSs em um mesmo CNPJ por mês para geração de pontos."
                                                                    unlimitedLabel="Sem limite"
                                                                    type="number"
                                                                    addonAfter="notas"
                                                                    isUnlimited={isUnlimitedNfsByCnpjPoints}
                                                                    onUnlimitedChange={unlimitedField.onChange}
                                                                    value={field.value}
                                                                    onChange={field.onChange}
                                                                    error={errors.nfs_max_month_quantity_by_cnpj_points}
                                                                    disabled={disableInputPoints("nfs_max_month_quantity_by_cnpj", "nfs_max_month_quantity_by_cnpj_points")}
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                />
                                            </Col>
                                        )}

                                        {showNFCConfig && (
                                            <Col {...invoiceColProps}>
                                                <Controller
                                                    name="nfc_max_month_quantity_by_cnpj_points"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Controller
                                                            name="is_unlimited_by_cnpj_nfc_divider_points"
                                                            control={control}
                                                            render={({ field: unlimitedField }) => (
                                                                <ConfigUnlimitedField
                                                                    label="NFC"
                                                                    tooltip="Quantidade limite de NFCs em um mesmo CNPJ por mês para geração de pontos."
                                                                    unlimitedLabel="Sem limite"
                                                                    type="number"
                                                                    addonAfter="notas"
                                                                    isUnlimited={isUnlimitedNfcByCnpjPoints}
                                                                    onUnlimitedChange={unlimitedField.onChange}
                                                                    value={field.value}
                                                                    onChange={field.onChange}
                                                                    error={errors.nfc_max_month_quantity_by_cnpj_points}
                                                                    disabled={disableInputPoints("nfc_max_month_quantity_by_cnpj", "nfc_max_month_quantity_by_cnpj_points")}
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                />
                                            </Col>
                                        )}
                                    </FieldsCard>
                                )}

                                <FieldsCard
                                    title="Adicionais dos Pontos"
                                    icon={<CalendarOutlined style={{ color: "#10b981", fontSize: 16 }} />}
                                    subtitle="Data de vigência e histórico de alterações"
                                    span={hasAtLeastOneInvoiceModule ? 12 : 24}
                                >
                                    {needsToInformPointsConfigEffectiveDate && (
                                        <Col xs={24} sm={10}>
                                            <Form.Item
                                                label="Data de Vigência"
                                                required
                                                validateStatus={errors.points_config_effective_date ? "error" : "success"}
                                                help={errors.points_config_effective_date?.message}
                                                tooltip="Data a partir da qual as novas configurações de pontos passarão a valer."
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Controller
                                                    name="points_config_effective_date"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            format={(date) => date.toLocaleDateString("pt-BR")}
                                                            value={field.value ? new Date(field.value) : undefined}
                                                            onChange={field.onChange}
                                                            style={{ width: "100%" }}
                                                        />
                                                    )}
                                                />
                                            </Form.Item>
                                        </Col>
                                    )}

                                    <Col xs={24} sm={needsToInformPointsConfigEffectiveDate ? 14 : 24}>
                                        <Form.Item
                                            label="Descrição das Configurações"
                                            validateStatus={errors.points_config_description ? "error" : "success"}
                                            help={errors.points_config_description?.message}
                                            tooltip="Identifica o motivo ou histórico das alterações feitas nos pontos."
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Controller
                                                name="points_config_description"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextArea
                                                        {...field}
                                                        rows={2}
                                                        placeholder="Ex: A cada R$ 50,00 gera 1 ponto..."
                                                        style={{ resize: "none" }}
                                                    />
                                                )}
                                            />
                                        </Form.Item>
                                    </Col>
                                </FieldsCard>
                            </Row>

                            {upcomingConfig?.points_config && upcomingConfig.points_config.length > 0 && (
                                <UpcomingConfigCarousel dataLength={upcomingConfig.points_config.length}>
                                    {upcomingConfig.points_config.map((cfg) => (
                                        <UpcomingConfigAlert
                                            key={cfg.id}
                                            title="Próximas configurações de pontos a entrar em vigência"
                                            config={cfg}
                                        />
                                    ))}
                                </UpcomingConfigCarousel>
                            )}
                        </div>
                    ) : (
                        <Alert
                            type="warning"
                            showIcon
                            message="Módulos de Arrecadação Necessários"
                            description="Selecione ao menos um módulo de geração de pontos (Notas Fiscais ou Impostos) no topo para liberar as configurações."
                            style={{ width: "100%" }}
                        />
                    )}
                </div>
            )}

            <div>
                <FieldsGroupTitle title="Bilhetes" showDivider />

                {hasAtLeastOnePointsGenerationModule ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        <Row gutter={[16, 16]}>
                            <FieldsCard
                                title="Divisores de Bilhetes"
                                icon={<TagOutlined style={{ color: "#3b82f6", fontSize: 16 }} />}
                                subtitle="Valor em reais (R$) necessário para gerar 1 bilhete da sorte"
                                span={12}
                            >
                                {showNFSConfig && (
                                    <Col {...divisorColProps}>
                                        <Controller
                                            name="nfs_tickets_divider"
                                            control={control}
                                            render={({ field }) => (
                                                <ConfigDivisorField
                                                    label="NFS"
                                                    tooltip="A cada R$ X em NFSs, o usuário ganhará 1 bilhete."
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={errors.nfs_tickets_divider}
                                                    disabled={disableInputTickets("nfs_divider", "nfs_tickets_divider")}
                                                />
                                            )}
                                        />
                                    </Col>
                                )}

                                {showNFCConfig && (
                                    <Col {...divisorColProps}>
                                        <Controller
                                            name="nfc_tickets_divider"
                                            control={control}
                                            render={({ field }) => (
                                                <ConfigDivisorField
                                                    label="NFC"
                                                    tooltip="A cada R$ X em NFCs, o usuário ganhará 1 bilhete."
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={errors.nfc_tickets_divider}
                                                    disabled={disableInputTickets("nfc_divider", "nfc_tickets_divider")}
                                                />
                                            )}
                                        />
                                    </Col>
                                )}

                                {showTaxesConfig && (
                                    <Col {...divisorColProps}>
                                        <Controller
                                            name="taxe_tickets_divider"
                                            control={control}
                                            render={({ field }) => (
                                                <ConfigDivisorField
                                                    label="Impostos Municipais"
                                                    tooltip="A cada R$ X em Impostos, o usuário ganhará 1 bilhete."
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={errors.taxe_tickets_divider}
                                                    disabled={disableInputTickets("taxe_divider", "taxe_tickets_divider")}
                                                />
                                            )}
                                        />
                                    </Col>
                                )}
                            </FieldsCard>

                            <FieldsCard
                                title="Valor Máximo para Gerar Bilhetes"
                                icon={<DollarOutlined style={{ color: "#3b82f6", fontSize: 16 }} />}
                                subtitle="Teto máximo por documento considerado para bilhetes"
                                span={12}
                            >
                                {showNFSConfig && (
                                    <Col {...divisorColProps}>
                                        <Controller
                                            name="nfs_max_amount_tickets"
                                            control={control}
                                            render={({ field }) => (
                                                <Controller
                                                    name="is_unlimited_nfs_divider_ticket"
                                                    control={control}
                                                    render={({ field: unlimitedField }) => (
                                                        <ConfigUnlimitedField
                                                            label="NFS"
                                                            tooltip="A partir de R$ X em NFSs, não são contabilizados mais bilhetes."
                                                            unlimitedLabel="Sem valor máx."
                                                            isUnlimited={isUnlimitedNfsTickets}
                                                            onUnlimitedChange={unlimitedField.onChange}
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            error={errors.nfs_max_amount_tickets}
                                                            disabled={disableInputTickets("nfs_max_amount", "nfs_max_amount_tickets")}
                                                        />
                                                    )}
                                                />
                                            )}
                                        />
                                    </Col>
                                )}

                                {showNFCConfig && (
                                    <Col {...divisorColProps}>
                                        <Controller
                                            name="nfc_max_amount_tickets"
                                            control={control}
                                            render={({ field }) => (
                                                <Controller
                                                    name="is_unlimited_nfc_divider_ticket"
                                                    control={control}
                                                    render={({ field: unlimitedField }) => (
                                                        <ConfigUnlimitedField
                                                            label="NFC"
                                                            tooltip="A partir de R$ X em NFCs, não são contabilizados mais bilhetes."
                                                            unlimitedLabel="Sem valor máx."
                                                            isUnlimited={isUnlimitedNfcTickets}
                                                            onUnlimitedChange={unlimitedField.onChange}
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            error={errors.nfc_max_amount_tickets}
                                                            disabled={disableInputTickets("nfc_max_amount", "nfc_max_amount_tickets")}
                                                        />
                                                    )}
                                                />
                                            )}
                                        />
                                    </Col>
                                )}

                                {showTaxesConfig && (
                                    <Col {...divisorColProps}>
                                        <Controller
                                            name="taxe_max_amount_tickets"
                                            control={control}
                                            render={({ field }) => (
                                                <Controller
                                                    name="is_unlimited_taxe_divider_ticket"
                                                    control={control}
                                                    render={({ field: unlimitedField }) => (
                                                        <ConfigUnlimitedField
                                                            label="Impostos Municipais"
                                                            tooltip="A partir de R$ X em Impostos, não são contabilizados mais bilhetes."
                                                            unlimitedLabel="Sem valor máx."
                                                            isUnlimited={isUnlimitedTaxeTickets}
                                                            onUnlimitedChange={unlimitedField.onChange}
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            error={errors.taxe_max_amount_tickets}
                                                            disabled={disableInputTickets("taxe_max_amount", "taxe_max_amount_tickets")}
                                                        />
                                                    )}
                                                />
                                            )}
                                        />
                                    </Col>
                                )}
                            </FieldsCard>
                        </Row>

                        <Row gutter={[16, 16]}>
                            {hasAtLeastOneInvoiceModule && (
                                <FieldsCard
                                    title="Quantidade Mensal Limite por Estabelecimento"
                                    icon={<BankOutlined style={{ color: "#3b82f6", fontSize: 16 }} />}
                                    subtitle="Máximo de notas de um mesmo CNPJ/mês que geram bilhetes"
                                    span={((showNFSConfig && showNFCConfig) || showTaxesAutoConfig) ? 12 : 12}
                                >
                                    {showNFSConfig && (
                                        <Col {...invoiceColProps}>
                                            <Controller
                                                name="nfs_max_month_quantity_by_cnpj_tickets"
                                                control={control}
                                                render={({ field }) => (
                                                    <Controller
                                                        name="is_unlimited_by_cnpj_nfs_divider_ticket"
                                                        control={control}
                                                        render={({ field: unlimitedField }) => (
                                                            <ConfigUnlimitedField
                                                                label="NFS"
                                                                tooltip="Quantidade limite de NFSs em um mesmo CNPJ por mês para geração de bilhetes."
                                                                unlimitedLabel="Sem limite"
                                                                type="number"
                                                                addonAfter="notas"
                                                                isUnlimited={isUnlimitedNfsByCnpjTickets}
                                                                onUnlimitedChange={unlimitedField.onChange}
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                error={errors.nfs_max_month_quantity_by_cnpj_tickets}
                                                                disabled={disableInputTickets("nfs_max_month_quantity_by_cnpj", "nfs_max_month_quantity_by_cnpj_tickets")}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Col>
                                    )}

                                    {showNFCConfig && (
                                        <Col {...invoiceColProps}>
                                            <Controller
                                                name="nfc_max_month_quantity_by_cnpj_tickets"
                                                control={control}
                                                render={({ field }) => (
                                                    <Controller
                                                        name="is_unlimited_by_cnpj_nfc_divider_ticket"
                                                        control={control}
                                                        render={({ field: unlimitedField }) => (
                                                            <ConfigUnlimitedField
                                                                label="NFC"
                                                                tooltip="Quantidade limite de NFCs em um mesmo CNPJ por mês para geração de bilhetes."
                                                                unlimitedLabel="Sem limite"
                                                                type="number"
                                                                addonAfter="notas"
                                                                isUnlimited={isUnlimitedNfcByCnpjTickets}
                                                                onUnlimitedChange={unlimitedField.onChange}
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                error={errors.nfc_max_month_quantity_by_cnpj_tickets}
                                                                disabled={disableInputTickets("nfc_max_month_quantity_by_cnpj", "nfc_max_month_quantity_by_cnpj_tickets")}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Col>
                                    )}
                                </FieldsCard>
                            )}

                            {((showNFSConfig && showNFCConfig) || showTaxesAutoConfig) && (
                                <FieldsCard
                                    title="Bônus e Multiplicadores"
                                    icon={<ThunderboltOutlined style={{ color: "#3b82f6", fontSize: 16 }} />}
                                    subtitle="Regras especiais de bilhete extra e bonificação de impostos"
                                    span={hasAtLeastOneInvoiceModule ? 12 : 24}
                                >
                                    {showNFSConfig && showNFCConfig && (
                                        <Col xs={24}>
                                            <div
                                                style={{
                                                    background: isDark ? "#222" : "#f8fafc",
                                                    border: `1px solid ${isDark ? "#2e2e2e" : "#e2e8f0"}`,
                                                    borderRadius: 8,
                                                    padding: "12px 14px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: 12,
                                                }}
                                            >
                                                <div>
                                                    <Typography.Text strong style={{ fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                                        Gerar bilhete na 1ª importação?
                                                    </Typography.Text>
                                                    <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 2 }}>
                                                        Gera 1 bilhete extra na primeira nota de cada sorteio.
                                                    </Typography.Text>
                                                </div>

                                                <Controller
                                                    name="generate_ticket_on_first_invoice_import"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Switch
                                                            checked={field.value !== false}
                                                            onChange={field.onChange}
                                                            checkedChildren="Sim"
                                                            unCheckedChildren="Não"
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </Col>
                                    )}

                                    {showTaxesAutoConfig && (
                                        <>
                                            <Col xs={24} sm={12}>
                                                <div style={{ marginBottom: 6 }}>
                                                    <Typography.Text strong style={{ fontSize: 12.5, color: isDark ? "#f0f0f0" : "#334155" }}>
                                                        Pagamento À Vista *
                                                    </Typography.Text>
                                                    <Typography.Text type="secondary" style={{ fontSize: 11.5, display: "block" }}>
                                                        Bilhetes por pagamento integral
                                                    </Typography.Text>
                                                </div>
                                                <Form.Item
                                                    style={{ marginBottom: 0 }}
                                                    validateStatus={errors.tickets_from_full_tax_payment ? "error" : undefined}
                                                    help={errors.tickets_from_full_tax_payment?.message}
                                                >
                                                    <Controller
                                                        name="tickets_from_full_tax_payment"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <InputNumber
                                                                {...field}
                                                                min={0}
                                                                addonAfter="bilhetes"
                                                                style={{ width: "100%" }}
                                                                disabled={disableInputTickets("tickets_from_full_tax_payment", "tickets_from_full_tax_payment")}
                                                            />
                                                        )}
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={24} sm={12}>
                                                <div style={{ marginBottom: 6 }}>
                                                    <Typography.Text strong style={{ fontSize: 12.5, color: isDark ? "#f0f0f0" : "#334155" }}>
                                                        Pagamento Parcial *
                                                    </Typography.Text>
                                                    <Typography.Text type="secondary" style={{ fontSize: 11.5, display: "block" }}>
                                                        Bilhetes por pagamento parcelado
                                                    </Typography.Text>
                                                </div>
                                                <Form.Item
                                                    style={{ marginBottom: 0 }}
                                                    validateStatus={errors.tickets_from_partial_tax_payment ? "error" : undefined}
                                                    help={errors.tickets_from_partial_tax_payment?.message}
                                                >
                                                    <Controller
                                                        name="tickets_from_partial_tax_payment"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <InputNumber
                                                                {...field}
                                                                min={0}
                                                                addonAfter="bilhetes"
                                                                style={{ width: "100%" }}
                                                                disabled={disableInputTickets("tickets_from_partial_tax_payment", "tickets_from_partial_tax_payment")}
                                                            />
                                                        )}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </>
                                    )}
                                </FieldsCard>
                            )}

                            {!((showNFSConfig && showNFCConfig) || showTaxesAutoConfig) && (
                                <FieldsCard
                                    title="Adicionais dos Bilhetes"
                                    icon={<CalendarOutlined style={{ color: "#3b82f6", fontSize: 16 }} />}
                                    subtitle="Data de vigência e histórico de alterações"
                                    span={12}
                                >
                                    {needsToInformTicketsConfigEffectiveDate && (
                                        <Col xs={24} sm={10}>
                                            <Form.Item
                                                label="Data de Vigência"
                                                required
                                                validateStatus={errors.tickets_config_effective_date ? "error" : "success"}
                                                help={errors.tickets_config_effective_date?.message}
                                                tooltip="Data a partir da qual as novas configurações dos bilhetes passarão a valer."
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Controller
                                                    name="tickets_config_effective_date"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            format={(date) => date.toLocaleDateString("pt-BR")}
                                                            value={field.value ? new Date(field.value) : undefined}
                                                            onChange={field.onChange}
                                                            style={{ width: "100%" }}
                                                        />
                                                    )}
                                                />
                                            </Form.Item>
                                        </Col>
                                    )}

                                    <Col xs={24} sm={needsToInformTicketsConfigEffectiveDate ? 14 : 24}>
                                        <Form.Item
                                            label="Descrição das Configurações"
                                            validateStatus={errors.tickets_config_description ? "error" : "success"}
                                            help={errors.tickets_config_description?.message}
                                            tooltip="Identifica o histórico ou justificativa de alterações feitas nos bilhetes."
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Controller
                                                name="tickets_config_description"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextArea
                                                        {...field}
                                                        rows={2}
                                                        placeholder="Ex: A cada R$ 50,00 gera 1 bilhete..."
                                                        style={{ resize: "none" }}
                                                    />
                                                )}
                                            />
                                        </Form.Item>
                                    </Col>
                                </FieldsCard>
                            )}
                        </Row>

                        {((showNFSConfig && showNFCConfig) || showTaxesAutoConfig) && (
                            <Row gutter={[16, 16]}>
                                <FieldsCard
                                    title="Adicionais dos Bilhetes"
                                    icon={<CalendarOutlined style={{ color: "#3b82f6", fontSize: 16 }} />}
                                    subtitle="Data de vigência e histórico de alterações"
                                    span={24}
                                >
                                    {needsToInformTicketsConfigEffectiveDate && (
                                        <Col xs={24} sm={8}>
                                            <Form.Item
                                                label="Data de Vigência"
                                                required
                                                validateStatus={errors.tickets_config_effective_date ? "error" : "success"}
                                                help={errors.tickets_config_effective_date?.message}
                                                tooltip="Data a partir da qual as novas configurações dos bilhetes passarão a valer."
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Controller
                                                    name="tickets_config_effective_date"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            format={(date) => date.toLocaleDateString("pt-BR")}
                                                            value={field.value ? new Date(field.value) : undefined}
                                                            onChange={field.onChange}
                                                            style={{ width: "100%" }}
                                                        />
                                                    )}
                                                />
                                            </Form.Item>
                                        </Col>
                                    )}

                                    <Col xs={24} sm={needsToInformTicketsConfigEffectiveDate ? 16 : 24}>
                                        <Form.Item
                                            label="Descrição das Configurações"
                                            validateStatus={errors.tickets_config_description ? "error" : "success"}
                                            help={errors.tickets_config_description?.message}
                                            tooltip="Identifica o histórico ou justificativa de alterações feitas nos bilhetes."
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Controller
                                                name="tickets_config_description"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextArea
                                                        {...field}
                                                        rows={2}
                                                        placeholder="Ex: A cada R$ 50,00 gera 1 bilhete..."
                                                        style={{ resize: "none" }}
                                                    />
                                                )}
                                            />
                                        </Form.Item>
                                    </Col>
                                </FieldsCard>
                            </Row>
                        )}

                        {upcomingConfig?.tickets_config && upcomingConfig.tickets_config.length > 0 && (
                            <UpcomingConfigCarousel dataLength={upcomingConfig.tickets_config.length}>
                                {upcomingConfig.tickets_config.map((cfg) => (
                                    <UpcomingConfigAlert
                                        key={cfg.id}
                                        title="Próximas configurações de bilhetes a entrar em vigência"
                                        config={cfg}
                                    />
                                ))}
                            </UpcomingConfigCarousel>
                        )}
                    </div>
                ) : (
                    <Alert
                        type="warning"
                        showIcon
                        message="Módulos de Arrecadação Necessários"
                        description="Selecione ao menos um módulo de geração de bilhetes (Notas Fiscais ou Impostos) no topo para liberar as configurações."
                        style={{ width: "100%" }}
                    />
                )}
            </div>

            {showRewardConfig && (
                <div>
                    <FieldsGroupTitle title="Validades e Expirações" showDivider />
                    <Row gutter={[16, 16]}>
                        <FieldsCard
                            title="Prazos de Validade dos Benefícios"
                            icon={<ClockCircleOutlined style={{ color: "#f59e0b", fontSize: 16 }} />}
                            subtitle="Configuração do tempo de expiração automática de pontos e cupons de desconto"
                            span={24}
                        >
                            <Col xs={24} sm={12}>
                                <div style={{ marginBottom: 6 }}>
                                    <Typography.Text strong style={{ fontSize: 12.5, color: isDark ? "#f0f0f0" : "#334155" }}>
                                        Validade dos Pontos *
                                    </Typography.Text>
                                    <Typography.Text type="secondary" style={{ fontSize: 11.5, display: "block" }}>
                                        Os pontos expiram automaticamente todos os dias à meia-noite.
                                    </Typography.Text>
                                </div>
                                <Form.Item
                                    style={{ marginBottom: 0 }}
                                    validateStatus={errors.time_expire_points ? "error" : undefined}
                                    help={errors.time_expire_points?.message}
                                >
                                    <Controller
                                        name="time_expire_points"
                                        control={control}
                                        defaultValue={365}
                                        render={({ field }) => (
                                            <InputNumber
                                                {...field}
                                                type="number"
                                                min={1}
                                                addonAfter="dias"
                                                style={{ width: "100%" }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <div style={{ marginBottom: 6 }}>
                                    <Typography.Text strong style={{ fontSize: 12.5, color: isDark ? "#f0f0f0" : "#334155" }}>
                                        Validade dos Vouchers *
                                    </Typography.Text>
                                    <Typography.Text type="secondary" style={{ fontSize: 11.5, display: "block" }}>
                                        Os vouchers resgatados expiram automaticamente todos os dias à meia-noite.
                                    </Typography.Text>
                                </div>
                                <Form.Item
                                    style={{ marginBottom: 0 }}
                                    validateStatus={errors.time_expire_vouchers ? "error" : undefined}
                                    help={errors.time_expire_vouchers?.message}
                                >
                                    <Controller
                                        name="time_expire_vouchers"
                                        control={control}
                                        defaultValue={30}
                                        render={({ field }) => (
                                            <InputNumber
                                                {...field}
                                                type="number"
                                                min={1}
                                                addonAfter="dias"
                                                style={{ width: "100%" }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </FieldsCard>
                    </Row>
                </div>
            )}

            <div>
                <FieldsGroupTitle title="Regras de Operação e Prazos" showDivider />
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                        <FieldsCard
                            title="Regras dos Sorteios"
                            icon={<TrophyOutlined style={{ color: "#8b5cf6", fontSize: 16 }} />}
                            subtitle="Comportamento e exibição pública dos resultados de sorteios"
                            span={24}
                        >
                            <Col xs={24}>
                                <Card size="small" hoverable style={{ background: isDark ? "#141414" : "#ffffff", border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}` }}>
                                    <Flex justify="space-between" align="center" gap={12}>
                                        <div>
                                            <Typography.Text strong style={{ fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                                Permitir múltiplos prêmios
                                            </Typography.Text>
                                            <Typography.Text type="secondary" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>
                                                O mesmo cidadão pode ser contemplado com mais de um prêmio no mesmo sorteio.
                                            </Typography.Text>
                                        </div>
                                        <Controller
                                            name="allow_user_multiple_prizes"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={!!field.value}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                    </Flex>
                                </Card>
                            </Col>

                            <Col xs={24}>
                                <Card size="small" hoverable style={{ background: isDark ? "#141414" : "#ffffff", border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}` }}>
                                    <Flex justify="space-between" align="center" gap={12}>
                                        <div>
                                            <Typography.Text strong style={{ fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                                Exibir nome do vencedor
                                            </Typography.Text>
                                            <Typography.Text type="secondary" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>
                                                Mostra o nome do cidadão contemplado na tela de resultados do sorteio.
                                            </Typography.Text>
                                        </div>
                                        <Controller
                                            name="show_winner_name"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={!!field.value}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                    </Flex>
                                </Card>
                            </Col>

                            {cityModules.includes(EModule.SOCIAL_INSTITUTION) && (
                                <Col xs={24}>
                                    <Card size="small" hoverable style={{ background: isDark ? "#141414" : "#ffffff", border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}` }}>
                                        <Flex justify="space-between" align="center" gap={12}>
                                            <div>
                                                <Typography.Text strong style={{ fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                                    Exibir instituição social
                                                </Typography.Text>
                                                <Typography.Text type="secondary" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>
                                                    Mostra a instituição social vinculada ao vencedor na tela de resultados do sorteio.
                                                </Typography.Text>
                                            </div>
                                            <Controller
                                                name="show_social_institution_name"
                                                control={control}
                                                render={({ field }) => (
                                                    <Switch
                                                        checked={!!field.value}
                                                        onChange={field.onChange}
                                                    />
                                                )}
                                            />
                                        </Flex>
                                    </Card>
                                </Col>
                            )}

                            <Col xs={24}>
                                <Card size="small" hoverable style={{ background: isDark ? "#141414" : "#ffffff", border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}` }}>
                                    <div style={{ marginBottom: 8 }}>
                                        <Typography.Text strong style={{ fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                            Intervalo mínimo para realização do sorteio
                                        </Typography.Text>
                                        <Typography.Text type="secondary" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>
                                            Dias após o encerramento do período de importação até que o sorteio possa ser apurado.
                                        </Typography.Text>
                                    </div>
                                    <Form.Item
                                        style={{ marginBottom: 0 }}
                                        validateStatus={errors.raffle_delay_days ? "error" : undefined}
                                        help={errors.raffle_delay_days?.message}
                                    >
                                        <Controller
                                            name="raffle_delay_days"
                                            control={control}
                                            render={({ field }) => (
                                                <InputNumber
                                                    {...field}
                                                    min={0}
                                                    addonAfter="dias"
                                                    style={{ width: "100%" }}
                                                    onChange={(value) => field.onChange(value ?? 0)}
                                                />
                                            )}
                                        />
                                    </Form.Item>
                                </Card>
                            </Col>
                        </FieldsCard>
                    </Col>
                    <Col xs={24} lg={12}>
                        <FieldsCard
                            title="Regras do Município"
                            icon={<SafetyCertificateOutlined style={{ color: "#0ea5e9", fontSize: 16 }} />}
                            subtitle="Requisitos de cadastro do cidadão e carência de notas"
                            span={24}
                        >
                            <Col xs={24}>
                                <Card size="small" hoverable style={{ background: isDark ? "#141414" : "#ffffff", border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}` }}>
                                    <Flex justify="space-between" align="center" gap={12}>
                                        <div>
                                            <Typography.Text strong style={{ fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                                Solicitar preenchimento de endereço
                                            </Typography.Text>
                                            <Typography.Text type="secondary" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>
                                                Ao acessar o aplicativo, exibe um pop-up solicitando o preenchimento de endereço pelo cidadão.
                                            </Typography.Text>
                                        </div>
                                        <Controller
                                            name="is_address_required"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={!!field.value}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                    </Flex>
                                </Card>
                            </Col>

                            <Col xs={24}>
                                <Card size="small" hoverable style={{ background: isDark ? "#141414" : "#ffffff", border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}` }}>
                                    <div style={{ marginBottom: 10 }}>
                                        <Typography.Text strong style={{ fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                            Dias para iniciar o processamento das notas
                                        </Typography.Text>
                                        <Typography.Text type="secondary" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>
                                            Após a importação, a nota aguardará a quantidade de dias informada antes de gerar bilhetes para o sorteio.
                                        </Typography.Text>
                                    </div>

                                    <Row gutter={[12, 12]}>
                                        <Col xs={24} sm={8}>
                                            <Form.Item
                                                label="NFC-e"
                                                validateStatus={errors.nfc_processing_delay_days ? "error" : undefined}
                                                help={errors.nfc_processing_delay_days?.message}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Controller
                                                    name="nfc_processing_delay_days"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <InputNumber
                                                            {...field}
                                                            min={0}
                                                            addonAfter="dias"
                                                            style={{ width: "100%" }}
                                                            onChange={(value) => field.onChange(value ?? 0)}
                                                        />
                                                    )}
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} sm={8}>
                                            <Form.Item
                                                label="NF-e"
                                                validateStatus={errors.nfe_processing_delay_days ? "error" : undefined}
                                                help={errors.nfe_processing_delay_days?.message}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Controller
                                                    name="nfe_processing_delay_days"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <InputNumber
                                                            {...field}
                                                            min={0}
                                                            addonAfter="dias"
                                                            style={{ width: "100%" }}
                                                            onChange={(value) => field.onChange(value ?? 0)}
                                                        />
                                                    )}
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} sm={8}>
                                            <Form.Item
                                                label="NFS-e"
                                                validateStatus={errors.nfs_processing_delay_days ? "error" : undefined}
                                                help={errors.nfs_processing_delay_days?.message}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Controller
                                                    name="nfs_processing_delay_days"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <InputNumber
                                                            {...field}
                                                            min={0}
                                                            addonAfter="dias"
                                                            style={{ width: "100%" }}
                                                            onChange={(value) => field.onChange(value ?? 0)}
                                                        />
                                                    )}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </FieldsCard>
                    </Col>
                </Row>
            </div>
        </div>
    );
}
